import {
  themeableWebGradientTokens,
  type ThemeableWebGradientTokenUnion,
  type ThemeableTokenNamesUnion,
} from "@planningcenter/doxy/ts"

interface TokenOverride {
  themeableTokens: readonly ThemeableTokenNamesUnion[]
  newLightValue: string
  newDarkValue: string
}

/**
 * @param overrides - Array of token overrides, each containing themeable tokens names and their new light/dark values
 * @param brandLight - The light mode value for the brand color
 * @param brandDark - The dark mode value for the brand color
 *
 * @example
 * bodyStyles({
 *   overrides: [
 *     {
 *       themeableTokens: tokensByThemeSet.brand,
 *       newLightValue: brandColor,
 *       newDarkValue: brandDarkColor,
 *     },
 *     {
 *       themeableTokens: tokensByThemeSet.status,
 *       newLightValue: statusColor,
 *       newDarkValue: statusDarkColor,
 *     },
 *   ],
 *   brandLight: brandColor,
 *   brandDark: brandDarkColor,
 * })
 */
export function bodyStyles({
  overrides,
  brandLight,
  brandDark,
}: {
  overrides: TokenOverride[]
  brandLight: string
  brandDark: string
}) {
  const themedTokensLightMode = overrides
    .flatMap(({ themeableTokens, newLightValue }) =>
      themeableTokens.map(
        (token) =>
          `${token}: ${overrideTokenValue(token, newLightValue, "light")};`,
      ),
    )
    .join("\n      ")

  const themedTokensDarkMode = overrides
    .flatMap(({ themeableTokens, newDarkValue }) =>
      themeableTokens.map(
        (token) =>
          `${token}: ${overrideTokenValue(token, newDarkValue, "dark")};`,
      ),
    )
    .join("\n      ")

  return `
    body {
      --color-brand-light: ${brandLight};
      --color-brand-dark: ${brandDark};
      --color-brand-gradient-start: var(--color-brand);
      --color-brand-gradient-end: var(--color-brand);
    }

    :root {
      ${themedTokensLightMode}
    }

    body[data-color-scheme="dark"] {
      ${themedTokensDarkMode}
    }

    @media (prefers-color-scheme: dark) {
      body[data-color-scheme="system"] {
        ${themedTokensDarkMode}
      }
    }
  `
}

function overrideTokenValue(
  tokenName: ThemeableTokenNamesUnion,
  newValue: string,
  colorScheme: "light" | "dark",
) {
  const isGradientToken = tokenName in themeableWebGradientTokens

  if (isGradientToken) {
    const token =
      themeableWebGradientTokens[tokenName as ThemeableWebGradientTokenUnion]
    const gradient = token[colorScheme]

    return overrideGradientColor(gradient, newValue)
  }

  return newValue
}

function overrideGradientColor(gradient: string, newColor: string) {
  return gradient
    .replace(/hsla?\s*\([^)]+\)/gi, newColor) // Replace HSL color values
    .replace(/rgba?\s*\([^)]+\)/gi, newColor) // Replace RGBA color values
    .replace(/#[0-9a-fA-F]{3,8}\b/gi, newColor) // Replace hex color values
}
